import { useEffect, useRef, useState, useCallback, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { useSocket } from '../../Context/SocketContext';
import {
  Box,
  Button,
  Container,
  Flex,
  Icon,
  Image,
  Text,
} from '@chakra-ui/react';
import ConversationCard from '../ConversationCard/ConversationCard';
import backgroundImage from '../../assets/BackgroundSpark.svg';
import ChatBotFooter from '../ClassChat/ChatBotFooter';
import { MdRefresh } from 'react-icons/md';
import { scrollbarStyles } from '../Constants';

import DoubtSVG from '../../assets/Doubt.svg';
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi';

import uuid from 'short-uuid';
import { useAuth } from '../../Context/AuthContext';

const SmartNotesChatBody = ({ addPromptQuestions }) => {
  const handlePromptClick = e => {
    const promptID = e.target.id;

    if (promptID === 'smartNotes-template-summarize') {
      addPromptQuestions('Can you summarize the key points further?');
    } else if (promptID === 'smartNotes-template-explain') {
      addPromptQuestions('Can you explain this concept further?');
    }
  };

  return (
    <Box height="100%">
      <Text
        fontWeight="600"
        color="#1B2559"
        fontSize={{ base: '1.2rem', md: '2rem' }}
      >
        Ask Follow Up Questions
      </Text>

      <Flex
        direction="column"
        justifyContent="space-between"
        height="calc(100% - 3rem)"
      >
        <Container
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          mt="3rem"
        >
          <Image
            src={DoubtSVG}
            w={{ base: '2rem', md: '4rem' }}
            h={{ base: '2rem', md: '4rem' }}
          />
          <Text color="#BDC2CA" fontWeight="400" width="200px" pt="1rem">
            Ask questions about your SMART Notes, and they will be answered here
          </Text>
          <Button
            boxShadow="0px 8px 20px 0px #7090B033"
            bg="white"
            color="#120F43"
            mt="1rem"
            w="240px"
            id="smartNotes-template-summarize"
            onClick={handlePromptClick}
          >
            <Icon as={HiOutlineQuestionMarkCircle} margin="5px" />
            Summarize Key Points
          </Button>
          <Button
            boxShadow="0px 8px 20px 0px #7090B033"
            bg="white"
            color="#120F43"
            mt="1rem"
            w="240px"
            id="smartNotes-template-explain"
            onClick={handlePromptClick}
          >
            <Icon as={HiOutlineQuestionMarkCircle} margin="5px" />
            Explain concepts further
          </Button>
        </Container>
        <Text
          textAlign="center"
          color="#BDC2CA"
          fontWeight="400"
          fontSize="0.9rem"
        >
          👈 Tip: Highlight any text in the SMART Notes, and ask follow-up
          questions instantly
        </Text>
      </Flex>
    </Box>
  );
};

export const MultiturnChat = ({ menuBtnText, chatType = 'classchat' }) => {
  const { name } = useParams();
  const socket = useSocket();
  const [chat, setChat] = useState([]);
  const [partAnswer, setPartAnswer] = useState('');
  const [isLastChunk, setIsLastChunk] = useState(false);

  const chatContainerRef = useRef(null);
  const bottomRef = useRef(null);

  const { setTopLevelError } = useAuth();

  // Function to scroll to the bottom of the chat container
  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const addQuestion = useCallback(
    question => {
      if (socket) {
        setPartAnswer('');
        const urlParams = new URLSearchParams(window.location.search);
        const associationID = urlParams.get('id');
        const course = urlParams.get('course');

        // generate a random id for the question
        const questionID = uuid.generate();

        socket.emit('multiturnChat', {
          question,
          questionID,
          difficulty: menuBtnText,
          state: {
            type: chatType,
            courseName: name || course,
            associationID,
          },
          regenerate: false,
        });
        setChat(prev => {
          return [
            ...prev,
            {
              role: 'user',
              parts: [{ text: question }],
              questionID,
              isBookmarked: false,
            },
          ];
        });

        setIsLastChunk(false);
        scrollToBottom();
      } else {
        console.log('Socket not connected');
      }
    },
    [chatType, menuBtnText, name, socket]
  );

  const handleRegenerateResponse = () => {
    console.log('Regenerate Response clicked');
  };

  useEffect(() => {
    if (!socket) return;

    const urlParams = new URLSearchParams(window.location.search);
    const associationID = urlParams.get('id');
    const course = urlParams.get('course');

    socket.emit('retrieveHistory', {
      type: chatType,
      courseName: name || course,
      associationID,
      difficulty: menuBtnText,
    });

    socket.on('retrieveHistory', ({ history, success }) => {
      if (success) {
        setChat(history);
      } else {
        setTopLevelError(
          'Error in retrieving history. Please try again later.'
        );
      }
    });

    socket.on('multiturnChat', response => {
      const { success, question, answer, isLastChunk } = response;
      if (success) {
        setPartAnswer(prev => prev + answer);
        setIsLastChunk(isLastChunk);
      } else {
        // think of some way to denote this in UI
        // todo: add error message to UI
      }

      // setReleaseInput(true);
    });

    socket.on('smartnotesmenu', ({ question, state }) => {
      addQuestion(question);
    });

    // Clean up the socket event listener
    return () => {
      //   socket.off('classchathistory');
      socket.off('retrieveHistory');
      socket.off('multiturnChat');
      socket.off('smartnotesmenu');
    };
  }, [socket, name, chatType, addQuestion, menuBtnText, setTopLevelError]);

  useEffect(() => {
    if (partAnswer === '') return;

    setChat(prev => {
      const newChat = [...prev];

      if (newChat.length > 0) {
        const lastMessage = newChat[newChat.length - 1];
        if (lastMessage.role === 'model') {
          // append to the last message
          lastMessage.parts[0].text = partAnswer;
          return newChat;
        }
      }
      newChat.push({
        role: 'model',
        parts: [{ text: partAnswer }],
      });

      return newChat;
    });

    scrollToBottom();
  }, [partAnswer]);

  useEffect(() => {
    scrollToBottom();
  }, [chat]);

  return (
    <Flex direction="column" height="100%">
      <Box
        ref={chatContainerRef}
        height="calc(100% - 50px)"
        bg="white"
        overflowY="auto"
        overflowX="hidden"
        backgroundImage={`url(${backgroundImage})`}
        backgroundRepeat={'no-repeat'}
        backgroundPosition={'center'}
        sx={scrollbarStyles}
      >
        <Flex
          direction="column"
          w="100%"
          h="100%"
          mx="auto"
          mb={'auto'}
          maxWidth="1100px"
        >
          {chat.map((conversation, index) => {
            const isSystemPrompt =
              /<highlighted_text>[\s\S]*?<\/highlighted_text>/.test(
                conversation?.parts[0]?.text ?? ''
              ) && conversation?.role === 'user';
            return (
              <Fragment key={index}>
                {!isSystemPrompt && (
                  <ConversationCard
                    key={index}
                    conversation={conversation}
                    chatType={chatType}
                    // showThinking={index === chat.length - 1 && !isLastChunk}
                  />
                )}
              </Fragment>
            );
          })}

          {chat.length > 0 && isLastChunk && (
            <Flex justifyContent="center" p="0.8rem 0rem">
              <Button
                border="1px solid #E2E8F0"
                p="1.5rem 1.2rem"
                borderRadius="45px"
                bg="white"
                cursor="pointer"
                width="fit-content"
                onClick={handleRegenerateResponse}
                // onClick={}
              >
                <Flex>
                  <Icon
                    as={MdRefresh}
                    height="18px"
                    width="18px"
                    title="Regenerate Response"
                    mr="5px"
                  />
                  <Text>Regenerate response</Text>
                </Flex>
              </Button>
            </Flex>
          )}

          {chatType === 'smartnotes' && chat.length === 0 && (
            <SmartNotesChatBody addPromptQuestions={addQuestion} />
          )}
          <div className="abcd" ref={bottomRef}></div>
        </Flex>
      </Box>
      <Box
        height="50px"
        bg="white"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <ChatBotFooter addQuestion={addQuestion} isEnabled={true} />
      </Box>
    </Flex>
  );
};
